import { FileDown, Share2, Download, FileIcon, ChevronDown, ImageIcon, Trash2, Plus, Save, Link2, CreditCard, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { PlacaQuote, PlacaQuoteCustomItemAttachment, PlacaQuoteCustomItem } from '../../../types/placas';
import { getPublicPdfUrl, handleShareWhatsApp, formatCurrency } from './placaQuoteHelpers';
import { getPaymentStatusLabel, getPaymentStatusVariant } from '../quotes/quoteHelpers';
import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../config';
import { confirmCustomItemPrice, updateExtraItems, updateQuoteDiscount } from '../../../services/placas.service';
import { generatePlacaPaymentLink } from '../../../services/payment.service';
import { useToast } from '../../../contexts/ToastContext';

interface PlacaQuoteDetailsDialogProps {
  quote: PlacaQuote | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quoteStatusUpdating: boolean;
  onUpdateStatus: (quoteId: string, status: string) => Promise<void>;
  products?: any[];
  accessToken?: string;
  onCustomItemPriceConfirmed?: () => void;
  onQuoteUpdated?: () => void;
  onUpdateQuoteItem?: (itemId: string, data: { quantity: number }) => Promise<any>;
  onDeleteQuoteItem?: (itemId: string) => Promise<any>;
  onAddQuoteItem?: (quoteId: string, data: any) => Promise<any>;
}

interface CustomItemState {
  manualPrice: string;
  isSubmitting: boolean;
}

export function PlacaQuoteDetailsDialog({
  quote,
  open,
  onOpenChange,
  quoteStatusUpdating,
  onUpdateStatus,
  products = [],
  accessToken,
  onCustomItemPriceConfirmed,
  onQuoteUpdated,
  onUpdateQuoteItem,
  onDeleteQuoteItem,
  onAddQuoteItem,
}: PlacaQuoteDetailsDialogProps) {
  const toast = useToast();
  const [attachmentsMap, setAttachmentsMap] = useState<Record<string, PlacaQuoteCustomItemAttachment[]>>({});
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [customItemStates, setCustomItemStates] = useState<Record<string, CustomItemState>>({});
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [editingQuantities, setEditingQuantities] = useState<Record<string, number>>({});
  const [savingItemId, setSavingItemId] = useState<string | null>(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [addProductForm, setAddProductForm] = useState({ productId: '', quantity: 1, dimensionId: '' });
  const [addingProduct, setAddingProduct] = useState(false);

  // Extra items and discount state
  const [extraItems, setExtraItems] = useState<Array<{ description: string; value: string }>>([]);
  const [newExtraDescription, setNewExtraDescription] = useState('');
  const [newExtraValue, setNewExtraValue] = useState('');
  const [discountInput, setDiscountInput] = useState('');
  const [savingExtras, setSavingExtras] = useState(false);
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);

  // Initialize custom item states
  useEffect(() => {
    if (!quote || !open) return;

    const customItems = quote.customItems || [];
    const initialStates: Record<string, CustomItemState> = {};

    customItems.forEach((item) => {
      initialStates[item.id] = {
        manualPrice: '',
        isSubmitting: false,
      };
    });

    setCustomItemStates(initialStates);
  }, [quote, open]);

  // Initialize extra items and discount from quote
  useEffect(() => {
    if (!quote || !open) return;

    // Initialize extra items
    const quoteExtraItems = quote.extraItems || [];
    setExtraItems(quoteExtraItems.map(item => ({
      description: item.description,
      value: item.value.toString()
    })));

    // Initialize discount
    if (quote.discountType && quote.discountValue) {
      if (quote.discountType === 'percentage') {
        setDiscountInput(`${quote.discountValue}%`);
      } else {
        setDiscountInput(quote.discountValue.toString());
      }
    } else {
      setDiscountInput('');
    }
  }, [quote, open]);

  // Fetch attachments for all custom items when dialog opens
  useEffect(() => {
    if (!quote || !open) return;

    const customItems = quote.customItems || [];
    if (customItems.length === 0) return;

    const fetchAttachments = async () => {
      setLoadingAttachments(true);
      try {
        const results = await Promise.all(
          customItems.map(async (item) => {
            const response = await fetch(`${API_BASE_URL}/placas/uploads/custom-item/${item.id}`);
            if (response.ok) {
              const attachments: PlacaQuoteCustomItemAttachment[] = await response.json();
              return { itemId: item.id, attachments };
            }
            return { itemId: item.id, attachments: [] };
          })
        );

        const map: Record<string, PlacaQuoteCustomItemAttachment[]> = {};
        results.forEach(({ itemId, attachments }) => {
          map[itemId] = attachments;
        });
        setAttachmentsMap(map);
      } catch (error) {
        console.error('Failed to fetch attachments:', error);
      } finally {
        setLoadingAttachments(false);
      }
    };

    fetchAttachments();
  }, [quote, open]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownloadAttachment = (attachmentId: string) => {
    const url = `${API_BASE_URL}/placas/uploads/${attachmentId}/download`;
    window.open(url, '_blank');
  };

  const updateCustomItemState = (itemId: string, updates: Partial<CustomItemState>) => {
    setCustomItemStates((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], ...updates },
    }));
  };

  const handleConfirmPrice = async (item: PlacaQuoteCustomItem) => {
    const state = customItemStates[item.id];
    if (!state || !accessToken) return;

    const finalPrice = parseFloat(state.manualPrice);

    if (!finalPrice || finalPrice <= 0) return;

    updateCustomItemState(item.id, { isSubmitting: true });

    try {
      const payload = {
        confirmedPrice: finalPrice,
      };

      await confirmCustomItemPrice(item.id, payload, accessToken);

      // Refresh quote data (but keep dialog open for further edits)
      if (onCustomItemPriceConfirmed) {
        await onCustomItemPriceConfirmed();
      }
    } catch (error) {
      console.error('Erro ao confirmar preço:', error);
    } finally {
      updateCustomItemState(item.id, { isSubmitting: false });
    }
  };

  // Calculate MP preview items showing real prices with a separate discount line
  const getMpPreviewItems = () => {
    if (!quote) return { items: [], total: 0 };

    const standardItems = (quote.items || []).map((item) => ({
      id: item.id,
      title: item.productName,
      quantity: item.quantity,
      unitPrice: Math.round((item.subtotal / item.quantity) * 100) / 100,
      subtotal: Math.round(item.subtotal * 100) / 100,
      isDiscount: false,
    }));

    const customItems = (quote.customItems || []).map((ci) => {
      const basePrice = ci.priceConfirmed && ci.confirmedPrice ? ci.confirmedPrice : (ci.estimatedPrice || 0);
      return {
        id: ci.id,
        title: ci.description,
        quantity: ci.quantity,
        unitPrice: Math.round(basePrice * 100) / 100,
        subtotal: Math.round(basePrice * ci.quantity * 100) / 100,
        isDiscount: false,
      };
    });

    const extraItemsMapped = (quote.extraItems || []).map((ei) => ({
      id: ei.id,
      title: ei.description,
      quantity: 1,
      unitPrice: Math.round(ei.value * 100) / 100,
      subtotal: Math.round(ei.value * 100) / 100,
      isDiscount: false,
    }));

    const allItems: Array<{ id: string; title: string; quantity: number; unitPrice: number; subtotal: number; isDiscount: boolean }> = [
      ...standardItems,
      ...customItems,
      ...extraItemsMapped,
    ];

    // Add discount line if applicable
    if (quote.discountType && quote.discountValue) {
      const rawTotal = allItems.reduce((sum, i) => sum + i.subtotal, 0);
      const discountAmt = quote.discountType === 'percentage'
        ? rawTotal * (quote.discountValue / 100)
        : quote.discountValue;
      const rounded = Math.round(discountAmt * 100) / 100;
      allItems.push({
        id: 'discount',
        title: 'Desconto',
        quantity: 1,
        unitPrice: -rounded,
        subtotal: -rounded,
        isDiscount: true,
      });
    }

    const total = allItems.reduce((sum, i) => sum + i.subtotal, 0);

    return { items: allItems, total: Math.round(total * 100) / 100 };
  };

  const mpPreview = getMpPreviewItems();

  if (!quote) return null;

  const standardItems = quote.items || [];
  const customItems = quote.customItems || [];

  // Helper to get product image URL (from item or by looking up product)
  const getProductImageUrl = (item: any) => {
    if (item.productImageUrl) return item.productImageUrl;
    const product = products.find(p => p.id === item.productId);
    return product?.imageUrl || null;
  };

  // Get product description from products list
  const getProductDescription = (item: any) => {
    const product = products.find((p: any) => p.id === item.productId);
    return product?.description || null;
  };

  const toggleItemExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleSaveQuantity = async (itemId: string) => {
    if (!onUpdateQuoteItem || !editingQuantities[itemId]) return;

    setSavingItemId(itemId);
    try {
      const result = await onUpdateQuoteItem(itemId, { quantity: editingQuantities[itemId] });
      if (result?.success && onQuoteUpdated) {
        await onQuoteUpdated();
      }
      setEditingQuantities((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    } catch (error) {
      console.error('Erro ao salvar quantidade:', error);
    } finally {
      setSavingItemId(null);
    }
  };

  const handleDeleteItem = async (itemId: string, itemName: string) => {
    if (!onDeleteQuoteItem) return;
    if (!window.confirm(`Remover "${itemName}" do orçamento?`)) return;

    try {
      const result = await onDeleteQuoteItem(itemId);
      if (result?.success && onQuoteUpdated) {
        await onQuoteUpdated();
      }
    } catch (error) {
      console.error('Erro ao remover item:', error);
    }
  };

  const handleAddProduct = async () => {
    if (!onAddQuoteItem || !quote || !addProductForm.productId) return;

    setAddingProduct(true);
    try {
      const payload: { productId: string; quantity: number; dimensionId?: string } = {
        productId: addProductForm.productId,
        quantity: addProductForm.quantity
      };
      if (addProductForm.dimensionId) {
        payload.dimensionId = addProductForm.dimensionId;
      }
      const result = await onAddQuoteItem(quote.id, payload);
      if (result?.success && onQuoteUpdated) {
        await onQuoteUpdated();
      }
      setAddProductForm({ productId: '', quantity: 1, dimensionId: '' });
      setShowAddProduct(false);
    } catch (error) {
      console.error('Erro ao adicionar produto:', error);
    } finally {
      setAddingProduct(false);
    }
  };

  const handleAddExtraItem = () => {
    if (!newExtraDescription.trim() || !newExtraValue) return;

    const value = parseFloat(newExtraValue);
    if (isNaN(value) || value <= 0) return;

    setExtraItems([...extraItems, { description: newExtraDescription, value: newExtraValue }]);
    setNewExtraDescription('');
    setNewExtraValue('');
  };

  const handleRemoveExtraItem = (index: number) => {
    setExtraItems(extraItems.filter((_, i) => i !== index));
  };

  const handleSaveExtrasAndDiscount = async () => {
    if (!quote || !accessToken) return;

    setSavingExtras(true);
    try {
      // Save extra items
      const itemsToSave = extraItems.map(item => ({
        description: item.description,
        value: parseFloat(item.value)
      }));
      await updateExtraItems(quote.id, itemsToSave, accessToken);

      // Save discount
      await updateQuoteDiscount(quote.id, discountInput, accessToken);

      // Refresh quote data
      if (onQuoteUpdated) {
        await onQuoteUpdated();
      }
      toast.success('Alterações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar alterações:', error);
      toast.error('Erro ao salvar alterações. Tente novamente.');
    } finally {
      setSavingExtras(false);
    }
  };

  const handleGeneratePaymentLink = async (force = false) => {
    if (!quote || !accessToken) return;

    // If link already exists and not forcing, just copy to clipboard
    if (quote.mpPaymentUrl && !force) {
      try {
        await navigator.clipboard.writeText(quote.mpPaymentUrl);
        toast.success('Link de pagamento copiado!');
      } catch {
        toast.success('Link: ' + quote.mpPaymentUrl);
      }
      return;
    }

    setPaymentLinkLoading(true);
    try {
      const result = await generatePlacaPaymentLink(quote.id, accessToken, force);
      try {
        await navigator.clipboard.writeText(result.paymentUrl);
        toast.success(force ? 'Novo link gerado e copiado!' : 'Link de pagamento gerado e copiado!');
      } catch {
        toast.success('Link gerado! Copie manualmente: ' + result.paymentUrl);
      }
      if (onQuoteUpdated) {
        await onQuoteUpdated();
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao gerar link de pagamento';
      toast.error(errorMessage);
    } finally {
      setPaymentLinkLoading(false);
    }
  };

  // Calculate totals
  const standardItemsTotal = standardItems.reduce((sum, item) => sum + item.subtotal, 0);
  const customItemsTotal = customItems.reduce((sum, item) => sum + (item.subtotal || item.confirmedPrice || item.estimatedPrice || 0), 0);
  const extraItemsTotal = extraItems.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0);
  const hasCustomItems = customItems.length > 0;
  const hasExtraItems = extraItems.length > 0;
  const hasDiscount = quote && (quote.discountType && quote.discountValue);

  // Calculate discount amount for display
  let discountAmount = 0;
  if (hasDiscount && quote) {
    if (quote.discountType === 'percentage') {
      const subtotal = standardItemsTotal + customItemsTotal + extraItemsTotal;
      discountAmount = (subtotal * (quote.discountValue || 0)) / 100;
    } else {
      discountAmount = quote.discountValue || 0;
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[95vw] sm:max-w-3xl lg:max-w-5xl xl:max-w-6xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <DialogTitle>Detalhes do Orcamento {quote.quoteNumber}</DialogTitle>
            {quote.mpPaymentStatus && (
              <Badge variant={getPaymentStatusVariant(quote.mpPaymentStatus)} className="w-fit">
                Pagamento: {getPaymentStatusLabel(quote.mpPaymentStatus)}
              </Badge>
            )}
          </div>
          <DialogDescription className="break-words">
            Cliente: {quote.customerName} - {quote.customerEmail}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
          {/* Left Column - Quote Details */}
          <div className="space-y-4 min-w-0">
          {/* Customer Information */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">Telefone</Label>
              <p className="text-sm">{quote.customerPhone}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Documento</Label>
              <p className="text-sm">{quote.customerDocument}</p>
            </div>
            {quote.customerCompany && (
              <div>
                <Label className="text-xs text-muted-foreground">Empresa</Label>
                <p className="text-sm">{quote.customerCompany}</p>
              </div>
            )}
            {quote.customerPosition && (
              <div>
                <Label className="text-xs text-muted-foreground">Cargo</Label>
                <p className="text-sm">{quote.customerPosition}</p>
              </div>
            )}
            {quote.customerSegment && (
              <div>
                <Label className="text-xs text-muted-foreground">Segmento</Label>
                <p className="text-sm">{quote.customerSegment}</p>
              </div>
            )}
          </div>

          {/* Standard Items */}
          {standardItems.length > 0 && (
            <div>
              <Label className="text-sm font-medium">Itens do Orcamento</Label>
              <div className="space-y-2 mt-3">
                {standardItems.map((item) => {
                  const isExpanded = expandedItems.has(item.id);
                  const description = getProductDescription(item);
                  const imageUrl = getProductImageUrl(item);

                  return (
                    <div key={item.id} className="rounded-lg border overflow-hidden">
                      {/* Collapsed row — always visible */}
                      <div className="flex items-center">
                        <button
                          type="button"
                          onClick={() => toggleItemExpanded(item.id)}
                          className="flex-1 flex items-center gap-3 p-3 text-left hover:bg-muted/50 transition-colors"
                        >
                          {imageUrl ? (
                            <img src={imageUrl} alt={item.productName} className="w-10 h-10 object-cover rounded flex-shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                              <ImageIcon className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">
                              {item.productName}
                              {item.dimensionLabel && (
                                <span className="text-xs text-muted-foreground ml-1">({item.dimensionLabel}cm)</span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {item.quantity}x {formatCurrency(item.unitPrice)}
                            </div>
                          </div>
                          <span className="font-semibold text-sm flex-shrink-0">{formatCurrency(item.subtotal)}</span>
                          <ChevronDown className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                        {onDeleteQuoteItem && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteItem(item.id, item.productName)}
                            className="h-8 w-8 p-0 mr-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="border-t px-4 py-3 bg-muted/30 space-y-2 text-sm">
                          {/* Description */}
                          {description && (
                            <div>
                              <span className="text-xs text-muted-foreground">Descrição</span>
                              <p className="text-sm mt-0.5 whitespace-pre-line">{description}</p>
                            </div>
                          )}

                          {/* Quantity */}
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Quantidade</span>
                            {onUpdateQuoteItem ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  min="1"
                                  value={editingQuantities[item.id] ?? item.quantity}
                                  onChange={(e) =>
                                    setEditingQuantities((prev) => ({
                                      ...prev,
                                      [item.id]: parseInt(e.target.value) || 1
                                    }))
                                  }
                                  className="w-20 h-8 text-right"
                                />
                                {editingQuantities[item.id] !== undefined && editingQuantities[item.id] !== item.quantity && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleSaveQuantity(item.id)}
                                    disabled={savingItemId === item.id}
                                    className="h-8 px-2"
                                  >
                                    {savingItemId === item.id ? 'Salvando...' : <Save className="h-3 w-3" />}
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span>{item.quantity}</span>
                            )}
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Preço unitário</span>
                            <span>{formatCurrency(item.unitPrice)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2 font-medium">
                            <span>Subtotal</span>
                            <span>{formatCurrency(item.subtotal)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Add Product Section */}
              {onAddQuoteItem && (
                <div className="mt-4 space-y-3">
                  {!showAddProduct ? (
                    <Button
                      variant="outline"
                      onClick={() => setShowAddProduct(true)}
                      className="w-full"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Adicionar Produto
                    </Button>
                  ) : (
                    <Card className="border-2 border-dashed">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <Label className="text-sm font-medium">Novo Produto</Label>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowAddProduct(false);
                              setAddProductForm({ productId: '', quantity: 1, dimensionId: '' });
                            }}
                            className="h-6 px-2 text-xs"
                          >
                            Cancelar
                          </Button>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <Label className="text-sm">Produto</Label>
                            <div className="mt-1 max-h-48 overflow-y-auto rounded-md border">
                              {products
                                .filter((p) => p.active)
                                .map((product) => (
                                  <div
                                    key={product.id}
                                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0 ${
                                      addProductForm.productId === product.id ? 'bg-accent ring-1 ring-primary' : ''
                                    }`}
                                    onClick={() => {
                                      setAddProductForm((prev) => ({ ...prev, productId: product.id, dimensionId: '' }));
                                    }}
                                  >
                                    {product.imageUrl ? (
                                      <img
                                        src={product.imageUrl}
                                        alt={product.name}
                                        className="h-10 w-10 rounded object-cover flex-shrink-0"
                                      />
                                    ) : (
                                      <div className="h-10 w-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                                      </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium truncate">{product.name}</p>
                                      <p className="text-xs text-muted-foreground">{formatCurrency(product.price)}</p>
                                    </div>
                                    {product.description && (
                                      <p className="text-xs text-muted-foreground text-right max-w-[40%] line-clamp-2 flex-shrink-0">
                                        {product.description}
                                      </p>
                                    )}
                                  </div>
                                ))}
                            </div>
                          </div>

                          {addProductForm.productId && (() => {
                            const selectedProduct = (products || []).find(p => p.id === addProductForm.productId);
                            const activeDimensions = selectedProduct?.dimensions?.filter((d: any) => d.active) || [];
                            const needsDimension = activeDimensions.length > 0;
                            return (
                              <>
                                {needsDimension && (
                                  <div className="mt-4">
                                    <label className="text-sm font-medium text-foreground mb-2 block">Dimensão</label>
                                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                                      {activeDimensions
                                        .sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                                        .map((dim: any) => (
                                          <button
                                            key={dim.id}
                                            type="button"
                                            onClick={() => setAddProductForm(prev => ({ ...prev, dimensionId: dim.id }))}
                                            className={`p-2 rounded-lg border text-left transition-colors ${
                                              addProductForm.dimensionId === dim.id
                                                ? 'bg-accent border-primary ring-1 ring-primary'
                                                : 'bg-card border-border hover:border-muted-foreground'
                                            }`}
                                          >
                                            <span className="text-sm font-medium text-foreground">{dim.label}</span>
                                            <span className="text-xs text-muted-foreground block">
                                              {dim.widthCm}×{dim.heightCm}cm — R$ {dim.price.toFixed(2)}
                                            </span>
                                          </button>
                                        ))}
                                    </div>
                                  </div>
                                )}

                                <div>
                                  <Label htmlFor="add-quantity" className="text-sm">Quantidade</Label>
                                  <Input
                                    id="add-quantity"
                                    type="number"
                                    min="1"
                                    value={addProductForm.quantity}
                                    onChange={(e) =>
                                      setAddProductForm((prev) => ({
                                        ...prev,
                                        quantity: parseInt(e.target.value) || 1
                                      }))
                                    }
                                    className="mt-1"
                                  />
                                </div>

                                <Button
                                  onClick={handleAddProduct}
                                  disabled={!addProductForm.productId || addingProduct || (needsDimension && !addProductForm.dimensionId)}
                                  className="w-full"
                                >
                                  {addingProduct ? 'Adicionando...' : 'Adicionar'}
                                </Button>
                              </>
                            );
                          })()}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Custom Items Section */}
          {customItems.length > 0 && (
            <div className="mt-4 space-y-4">
              <Label className="text-sm font-medium">Itens Personalizados</Label>

              {customItems.map((item) => {
                const state = customItemStates[item.id] || {
                  manualPrice: '',
                  isSubmitting: false,
                };
                const attachments = attachmentsMap[item.id] || [];

                return (
                  <Card key={item.id} className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4 space-y-4">
                      {/* Item Details Header */}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="font-medium text-base">{item.description}</div>
                          <div className="text-sm text-muted-foreground mt-1 space-y-1">
                            {item.customWidthCm && item.customHeightCm && item.customThicknessMm && (
                              <div>Dimensões: {item.customWidthCm}cm × {item.customHeightCm}cm × {item.customThicknessMm}mm</div>
                            )}
                            {item.eventName && <div>Evento: {item.eventName}</div>}
                            <div>Quantidade: {item.quantity}</div>
                          </div>
                          {item.notes && (
                            <div className="text-sm text-muted-foreground mt-2 p-2 bg-muted/50 rounded">
                              <strong>Obs:</strong> {item.notes}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge variant={item.priceConfirmed ? 'default' : 'secondary'}>
                            {item.priceConfirmed ? 'Confirmado' : 'Pendente'}
                          </Badge>
                          {item.priceConfirmed && (
                            <span className="font-semibold text-lg text-primary">
                              {formatCurrency(item.subtotal || item.confirmedPrice || 0)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* File Attachments for this item */}
                      {attachments.length > 0 && (
                        <div className="space-y-2 border-t pt-3">
                          <Label className="text-xs text-muted-foreground">Arquivos Anexados</Label>
                          <div className="space-y-2">
                            {attachments.map((attachment) => (
                              <div
                                key={attachment.id}
                                className="flex items-center justify-between p-2 bg-muted/30 rounded border text-sm"
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <FileIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium truncate">
                                      {attachment.fileName}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {formatFileSize(attachment.fileSize)}
                                    </div>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadAttachment(attachment.id)}
                                  className="flex-shrink-0 h-8 w-8 p-0"
                                >
                                  <Download className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Manual Price Input (always editable) */}
                      <div className="border-t pt-4 space-y-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">Configuração de Preço</Label>
                          </div>

                          {/* Manual Price Input */}
                          <div className="space-y-3">
                            <div>
                              <Label htmlFor={`price-${item.id}`} className="text-sm">Preço Unitário (R$)</Label>
                              <Input
                                id={`price-${item.id}`}
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                value={state.manualPrice}
                                onChange={(e) =>
                                  updateCustomItemState(item.id, { manualPrice: e.target.value })
                                }
                                className="mt-1"
                              />
                            </div>
                            {parseFloat(state.manualPrice) > 0 && (
                              <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 p-3 border border-blue-200 dark:border-blue-900 space-y-1">
                                <div className="flex justify-between text-sm">
                                  <span>Preço Unitário:</span>
                                  <span className="font-medium">R$ {parseFloat(state.manualPrice).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-sm font-bold">
                                  <span>Subtotal ({item.quantity}×):</span>
                                  <span>R$ {(parseFloat(state.manualPrice) * item.quantity).toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Confirm Button */}
                          <Button
                            onClick={() => handleConfirmPrice(item)}
                            disabled={
                              state.isSubmitting ||
                              !state.manualPrice || parseFloat(state.manualPrice) <= 0
                            }
                            className="w-full"
                          >
                            {state.isSubmitting ? 'Confirmando...' : item.priceConfirmed ? 'Atualizar Preço' : 'Confirmar Preço'}
                          </Button>
                        </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Extra Items Section */}
          <div className="mt-4 space-y-4 border rounded-lg p-4 bg-muted/20">
            <div>
              <h3 className="font-semibold text-sm">Itens Personalizados</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Adicione custos extras como frete, mão de obra, acabamentos, etc.
              </p>
            </div>

            {/* Extra Items List */}
            {extraItems.length > 0 && (
              <div className="space-y-2">
                {extraItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 border rounded p-2 bg-background">
                    <Input
                      value={item.description}
                      onChange={(e) => {
                        const newItems = [...extraItems];
                        newItems[index] = { ...newItems[index], description: e.target.value };
                        setExtraItems(newItems);
                      }}
                      placeholder="Descrição"
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.value}
                      onChange={(e) => {
                        const newItems = [...extraItems];
                        newItems[index] = { ...newItems[index], value: e.target.value };
                        setExtraItems(newItems);
                      }}
                      placeholder="Valor"
                      className="w-32"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveExtraItem(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Extra Item */}
            <div className="flex gap-2">
              <Input
                value={newExtraDescription}
                onChange={(e) => setNewExtraDescription(e.target.value)}
                placeholder="Ex: Frete, Corte especial"
                className="flex-1"
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                value={newExtraValue}
                onChange={(e) => setNewExtraValue(e.target.value)}
                placeholder="Ex: 50.00"
                className="w-32"
              />
              <Button
                type="button"
                onClick={handleAddExtraItem}
                disabled={!newExtraDescription.trim() || !newExtraValue}
                size="sm"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Discount Section */}
          <div className="space-y-2 border rounded-lg p-4 bg-muted/20">
            <Label className="text-sm font-semibold">Desconto (opcional)</Label>
            <Input
              value={discountInput}
              onChange={(e) => setDiscountInput(e.target.value)}
              placeholder='Digite "10%" para 10% ou "50" para R$ 50'
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Use "%" para desconto percentual (ex: "10%") ou valor fixo em reais (ex: "50")
            </p>
          </div>

          {/* Save Button for Extras and Discount */}
          <Button
            onClick={handleSaveExtrasAndDiscount}
            disabled={savingExtras}
            className="w-full"
          >
            {savingExtras ? 'Salvando...' : 'Salvar Alterações'}
          </Button>

          {/* Totals Section */}
          <div className="mt-4 space-y-2 border-t pt-4">
            {(hasCustomItems || hasExtraItems || hasDiscount) ? (
              <>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Subtotal (Produtos):</span>
                  <span>{formatCurrency(standardItemsTotal)}</span>
                </div>
                {hasCustomItems && customItemsTotal > 0 && (
                  <div className="flex justify-between items-center text-sm text-blue-600">
                    <span>Itens Personalizados:</span>
                    <span>+{formatCurrency(customItemsTotal)}</span>
                  </div>
                )}
                {hasExtraItems && extraItemsTotal > 0 && (
                  <div className="flex justify-between items-center text-sm text-blue-600">
                    <span>Itens Extras:</span>
                    <span>+{formatCurrency(extraItemsTotal)}</span>
                  </div>
                )}
                {hasDiscount && discountAmount > 0 && (
                  <div className="flex justify-between items-center text-sm text-red-600">
                    <span>Desconto:</span>
                    <span>-{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="font-semibold text-lg">Total:</span>
                  <span className="text-xl font-bold">{formatCurrency(quote.totalAmount)}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between items-center">
                <span className="font-semibold text-lg">Total:</span>
                <span className="text-xl font-bold">{formatCurrency(quote.totalAmount)}</span>
              </div>
            )}
          </div>

          {/* Status Change Buttons */}
          <div className="space-y-2">
            <Label>Alterar Status</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={quote.status === 'pending' ? 'pending' : 'outline'}
                onClick={() => onUpdateStatus(quote.id, 'pending')}
                disabled={quoteStatusUpdating}
                className="flex-1 sm:flex-initial"
              >
                Pendente
              </Button>
              <Button
                variant={quote.status === 'approved' ? 'approved' : 'outline'}
                onClick={() => onUpdateStatus(quote.id, 'approved')}
                disabled={quoteStatusUpdating}
                className="flex-1 sm:flex-initial"
              >
                Aprovar
              </Button>
              <Button
                variant={quote.status === 'rejected' ? 'rejected' : 'outline'}
                onClick={() => onUpdateStatus(quote.id, 'rejected')}
                disabled={quoteStatusUpdating}
                className="flex-1 sm:flex-initial"
              >
                Rejeitar
              </Button>
            </div>
          </div>
          </div>

          {/* Right Column - Mercado Pago Preview */}
          <div className="hidden lg:block">
            <Card className="sticky top-0 border-blue-500/30 bg-blue-950/20">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-blue-400">
                  <CreditCard className="h-4 w-4" />
                  Preview Mercado Pago
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Itens como aparecem no checkout do cliente
                </p>

                <div className="space-y-2">
                  {mpPreview.items.map((item) => (
                    <div key={item.id} className={`rounded-md border p-2.5 space-y-1 ${item.isDiscount ? 'border-destructive/30 bg-destructive/5' : 'border-border/50 bg-background/50'}`}>
                      <p className={`text-xs font-medium leading-tight ${item.isDiscount ? 'text-destructive' : ''}`}>{item.title}</p>
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                        {item.isDiscount ? (
                          <>
                            <span className="text-destructive">Desconto aplicado</span>
                            <span className="font-medium text-destructive">-R$ {Math.abs(item.subtotal).toFixed(2)}</span>
                          </>
                        ) : (
                          <>
                            <span>{item.quantity}x R$ {item.unitPrice.toFixed(2)}</span>
                            <span className="font-medium text-foreground">R$ {item.subtotal.toFixed(2)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-border/50 pt-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Total</span>
                  <span className="text-sm font-bold">R$ {mpPreview.total.toFixed(2)}</span>
                </div>

                {quote.mpPaymentStatus && (
                  <div className="border-t border-border/50 pt-2 space-y-1.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">Status:</span>
                      <Badge variant={getPaymentStatusVariant(quote.mpPaymentStatus)} className="text-[10px] px-1.5 py-0">
                        {getPaymentStatusLabel(quote.mpPaymentStatus)}
                      </Badge>
                    </div>
                    {quote.mpPaymentUpdatedAt && (
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-muted-foreground">Atualizado:</span>
                        <span className="text-foreground">{new Date(quote.mpPaymentUpdatedAt).toLocaleString('pt-BR')}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="text-[10px] text-muted-foreground/60 pt-1">
                  Ref: {quote.quoteNumber}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {quote.pdfToken && (
              <>
                <Button
                  variant="default"
                  onClick={() => quote.pdfToken && window.open(getPublicPdfUrl(quote.pdfToken), '_blank')}
                  disabled={!quote}
                  className="w-full sm:w-auto"
                >
                  <FileDown className="mr-2 h-4 w-4" />
                  Baixar PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleShareWhatsApp(quote)}
                  disabled={!quote}
                  className="w-full sm:w-auto"
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  Compartilhar WhatsApp
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleGeneratePaymentLink(false)}
                  disabled={paymentLinkLoading || !quote || !accessToken}
                  className="w-full sm:w-auto"
                >
                  <Link2 className="mr-2 h-4 w-4" />
                  {paymentLinkLoading
                    ? 'Gerando...'
                    : quote?.mpPaymentUrl
                      ? 'Copiar Link Pagamento'
                      : 'Gerar Link Pagamento'}
                </Button>
                {quote?.mpPaymentUrl && (
                  <Button
                    variant="outline"
                    onClick={() => handleGeneratePaymentLink(true)}
                    disabled={paymentLinkLoading || !accessToken}
                    className="w-full sm:w-auto"
                    title="Gerar novo link com dados atualizados"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Regenerar Link
                  </Button>
                )}
              </>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
              Fechar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
